import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { HOME_LAYOUT_KEY, homeApi } from '@/api/home.api';
import { findHubTile, type HubTile } from '@/components/layout/setupHubs';
import type { NavGroup } from '@/components/layout/navConfig';

/**
 * FR-NAV-03/04/07: the signed-in person's Home layout — card order, shortcuts and the daily items their role pins to the
 * left menu. One query shared by Home, the sidebar, the top navigation and the phone bottom bar.
 */
export function useHomeLayout() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [HOME_LAYOUT_KEY],
    queryFn: homeApi.layout,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
}

/** Whether the person may open a hub tile: its permission and the company's feature switch, as the hub page checks. */
export function useTileAllowed() {
  const { hasAnyPermission } = useAuth();
  const { isFeatureEnabled } = useFeatureGate();
  return useCallback(
    (t: HubTile) => (!t.perms?.length || hasAnyPermission(t.perms)) && isFeatureEnabled(t.feature),
    [hasAnyPermission, isFeatureEnabled],
  );
}

/** Hub keys turned into the tiles the person may open, in the given order; unknown keys are skipped. */
export function useAllowedTiles(keys: string[] | undefined): HubTile[] {
  const allowed = useTileAllowed();
  return useMemo(() => {
    const seen = new Set<string>();
    return (keys ?? []).reduce<HubTile[]>((acc, key) => {
      const tile = findHubTile(key);
      if (tile && !seen.has(key) && allowed(tile)) {
        seen.add(key);
        acc.push(tile);
      }
      return acc;
    }, []);
  }, [keys, allowed]);
}

/** FR-NAV-03/04: the role's pinned daily items as direct-link menu entries, ready to sit right after Home. */
export function useRolePinGroups(): NavGroup[] {
  const layout = useHomeLayout();
  const tiles = useAllowedTiles(layout.data?.rolePins);
  return useMemo(() => tiles.map((t) => ({ label: t.label, icon: t.icon, to: t.to })), [tiles]);
}

/** Puts the role pins right after Home, skipping any whose label already sits in the menu. */
export function withRolePins(groups: NavGroup[], pins: NavGroup[]): NavGroup[] {
  if (!pins.length) return groups;
  const labels = new Set(groups.map((g) => g.label));
  const extra = pins.filter((p) => !labels.has(p.label));
  const home = groups.findIndex((g) => g.to === '/dashboard');
  const at = home + 1; // -1 + 1 = 0: no Home, pins lead
  return [...groups.slice(0, at), ...extra, ...groups.slice(at)];
}
